import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { getConsent } from "./CookieConsent";

/**
 * AdSlot
 * - Renders an AdSense ad unit.
 * - GDPR consent for EU/UK/CH is handled by Google's certified CMP
 *   (loaded automatically by the AdSense script). No custom consent gate.
 */

let _scriptLoaded = false;

function loadAdSenseScript(clientId) {
  if (_scriptLoaded || !clientId || typeof window === "undefined") return;
  if (document.querySelector('script[data-bdd-adsense="1"]')) {
    _scriptLoaded = true;
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.dataset.bddAdsense = "1";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
  document.head.appendChild(s);
  _scriptLoaded = true;
}

export default function AdSlot({ id = "ad", size = "banner", className = "" }) {
  const ref = useRef(null);
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api.get("/public-settings").then((r) => r.data),
    staleTime: 60_000,
  });
  const clientId = settings?.adsense_client_id || "";

  // Track cookie-consent state — Ads may only load when user has granted `ads` consent.
  const [adsConsent, setAdsConsent] = useState(() => {
    const c = getConsent();
    return !!(c && c.ads);
  });
  useEffect(() => {
    const refresh = () => {
      const c = getConsent();
      setAdsConsent(!!(c && c.ads));
    };
    window.addEventListener("bdd_consent_changed", refresh);
    // Also react to consent updates from other tabs
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("bdd_consent_changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    if (!clientId) return;
    if (!adsConsent) return; // GDPR: do not load AdSense until user accepts
    // Skip if container is not visible (avoids "No slot size for availableWidth=0" errors)
    if (ref.current) {
      const w = ref.current.getBoundingClientRect().width;
      if (w < 50) return;
    }
    loadAdSenseScript(clientId);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      /* adsbygoogle not ready yet — Auto Ads will fill in */
    }
  }, [clientId, adsConsent]);

  // Map our internal sizes to constrained AdSense formats so Google can't
  // blow up an ad into a 600+px tall rectangle that breaks the dark theme.
  const adConfig = {
    leaderboard: { format: "horizontal", maxH: 120, minH: 90 },
    square: { format: "rectangle", maxH: 280, minH: 250 },
    sidebar: { format: "vertical", maxH: 600, minH: 250 },
    inline: { format: "horizontal", maxH: 120, minH: 90 },
  }[size] || { format: "horizontal", maxH: 120, minH: 90 };

  const dims = {
    leaderboard: "min-h-[120px] max-h-[140px]",
    square: "min-h-[260px] max-h-[300px]",
    sidebar: "min-h-[600px]",
    inline: "min-h-[120px] max-h-[140px]",
  }[size] || "min-h-[120px] max-h-[140px]";

  // Active AdSense unit
  if (clientId) {
    return (
      <div
        data-testid={`ad-slot-${id}`}
        className={`w-full ${dims} ${className} overflow-hidden rounded-xl bg-zinc-900/40 border border-zinc-800/60`}
      >
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6c7e91] py-1.5 text-center">
          Sponsored
        </div>
        <ins
          ref={ref}
          className="adsbygoogle block"
          style={{
            display: "block",
            minHeight: adConfig.minH,
            maxHeight: adConfig.maxH,
            width: "100%",
          }}
          data-ad-client={clientId}
          data-ad-slot="auto"
          data-ad-format={adConfig.format}
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // Placeholder until AdSense client id is configured
  return (
    <div
      data-testid={`ad-slot-${id}`}
      className={`w-full bg-[#17212b]/60 border border-dashed border-[#243447] rounded-xl flex flex-col items-center justify-center p-6 text-[#6c7e91] text-xs font-mono-bdd uppercase tracking-[0.25em] ${dims} ${className}`}
    >
      <span className="text-[10px] text-[#3d4d63] mb-2">Sponsored</span>
      <span className="text-[#6c7e91]">Ad space · {size}</span>
      <span className="text-[10px] text-[#3d4d63] mt-2">slot: {id}</span>
    </div>
  );
}
